<template>
  <div class="summary-intro">
    <TruncatedText
      v-if="summary"
      :text="summary"
      :length="480"
    />

    <Callout
      v-else
      status="info"
      class="no-summary"
    >
      <p>There is no introduction for this person.</p>
      <Button
        :loading="isGenerating"
        icon-before="fas fa-sync"
        spacing="tight"
        size="3_5"
        :disabled="successful"
        @click="generateFactSheet"
      >
        Generate one
      </Button>
    </Callout>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Button, Callout, TruncatedText } from '@stakeui';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { ToastProgrammatic as Toast } from 'buefy';

const advisorStore = useAdvisorStore();

const props = defineProps({
  entityExternalId: {
    type: String,
    required: true,
  },
  summary: {
    type: String,
    default: '',
  },
});

const isGenerating = ref(false);
const hasGenerated = ref(false);
const successful = ref(false);

const generateFactSheet = async () => {
  isGenerating.value = true;

  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/fact-sheet/regenerate/`, {
      method: 'POST',
      headers: advisorStore.headers,
    });
    const data = await response.json();

    if (data.status === 'success' && data.fact_sheet?.summary) {
      Toast.open({
        message: `Introduction generated. Reloading the page…`,
        type: 'is-success',
      });
      successful.value = true;
      return window.location.reload();
    }

    Toast.open({
      message: `Error generating an introduction.`,
      type: 'is-danger',
    });
  } catch (error) {
    console.error('Error regenerating an introduction:', error);

    Toast.open({
      message: `Error generating an introduction.`,
      type: 'is-danger',
    });
  } finally {
    hasGenerated.value = true;
    isGenerating.value = false;
  }
};
</script>

<style scoped>
.summary-intro {
  font-size: 1.125em;
  display: flex;
  flex-direction: column;
}

.no-summary {
  font-size: 1rem;
  font-style: italic;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  text-align: center;
  min-height: 5em; /* 2 lines */

  :deep(.stk-callout-body) {
    align-items: center;
    flex-direction: column;
    display: flex;
    align-items: center;
    gap: 0.25em;
  }
}
</style>
